import { breakpoints } from "@tvg/design-system";
import styled, { css } from "styled-components";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RunnerModifierContainer = styled.div<{
  isDesktopPicksFormat: boolean;
}>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  ${({ isDesktopPicksFormat }) =>
    isDesktopPicksFormat &&
    css`
      flex-direction: row-reverse;
    `};
`;

export const RunnerModifierExtension = styled.div<{
  isDesktopPicksFormat: boolean;
}>`
  display: flex;
  flex-direction: column;

  @media screen and ${breakpoints.tablet.max.sm} {
    min-width: initial;
    padding: ${({ theme }) => theme.space["space-1"]}
      ${({ theme }) => theme.space["space-4"]};
  }

  ${({ isDesktopPicksFormat, theme }) =>
    !isDesktopPicksFormat &&
    css`
      padding: ${theme.space["space-4"]};
      background-color: ${theme.colorTokens.background.layer};
      border-bottom: 1px solid ${theme.colorTokens.border.default};
      border-top: 1px solid ${theme.colorTokens.border.default};
    `};
`;

export const OptionGroup = styled.div<{ hasSeparator: boolean }>`
  min-width: 120px;
  ${({ hasSeparator }) =>
    hasSeparator &&
    css`
      border-left: 1px solid var(--fd-colors-border-default);
    `}

  @media screen and ${breakpoints.tablet.max.sm} {
    min-width: initial;
    flex: 1 0 0;
  }
`;

export const OptionGroupsBox = styled.div`
  display: flex;
  flex-direction: row;
  border-top: 1px solid var(--fd-colors-border-default);
`;

export const SelectAllContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  @media screen and ${breakpoints.tablet.min.sm} {
    flex-grow: 4;
  }

  @media screen and ${breakpoints.tablet.max.sm} {
    margin-left: auto;
  }
`;

export const SimpleSelectAllContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  column-gap: ${({ theme }) => theme.space["space-1"]};
`;

export const SimpleSelectAllButton = styled.button<{
  isAllSelected: boolean;
}>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 25px;
  padding: 5px 7px;
  background-color: ${({ theme, isAllSelected }) =>
    isAllSelected
      ? theme.colorTokens.component.button.secondary.background.hover
      : theme.colorTokens.component.input.surface};
  border: 1px solid
    ${({ theme, isAllSelected }) =>
      isAllSelected
        ? theme.colorTokens.component.button.tertiary.border.active
        : theme.colorTokens.component.button.tertiary.border.base};
  border-radius: 2px;
  box-shadow: 0 1px 3px rgb(17 43 68 / 12%);
  cursor: pointer;

  &:hover {
    background-color: ${({ theme, isAllSelected }) =>
      isAllSelected
        ? theme.colorTokens.component.button.secondary.background.base
        : theme.colorTokens.component.button.buttonLink.background.hover};
  }
`;

export const DropdownSelectAllContainer = styled.div`
  position: absolute;
  overflow: hidden;
  z-index: 3;
  display: flex;
  width: 100%;
  flex-direction: column;
  top: 43px;
  background-color: ${({ theme }) => theme.colorTokens.background.surface};
  border: 1px solid ${({ theme }) => theme.colorTokens.border.default};
  box-shadow: 0 1px 3px rgba(17 43 68 12%);
  border-radius: 4px;
`;

export const DropdownSelectAllButton = styled.button<{
  isSelected: boolean;
}>`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  height: 42px;
  padding: ${({ theme }) => theme.space["space-4"]};
  background-color: ${({ theme, isSelected }) =>
    isSelected
      ? theme.colorTokens.component.button.tertiary.background.hover
      : theme.colorTokens.component.button.tertiary.background.base};
  ${({ theme, isSelected }) =>
    isSelected &&
    css`
      border-left: 2px solid ${theme.colorTokens.content.link};
    `}

  &:hover {
    background-color: ${({ theme }) =>
      theme.colorTokens.component.button.tertiary.background.hover};
  }
`;

export const ModalButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.space["space-4"]};
  border-top: 1px solid ${({ theme }) => theme.colorTokens.border.default};
`;

export const Divider = styled.div`
  display: inline-flex;
  align-self: center;
  width: 1px;
  height: 12px;
  margin: 0 ${({ theme }) => theme.space["space-1"]};
  background-color: ${({ theme }) => theme.colorTokens.content.subtle3};
`;

export const SegmentedControlContainer = styled.div`
  flex-direction: column;
  overflow: hidden;
  padding: 0 ${({ theme }) => theme.space["space-4"]};
  background-color: ${({ theme }) => theme.colorTokens.background.surface};
`;

export const RaceCardViewButtonContainer = styled.div<{
  isActive?: boolean;
}>`
  display: flex;
  flex-shrink: 0;
  border-radius: 4px;
  background-color: ${({ isActive }) =>
    isActive
      ? "var(--fd-colors-component-button-button-link-background-active)"
      : "transparent"};
`;

export const RunnerModifierWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${({ theme }) => theme.colorTokens.background.surface};
`;

export const ButtonContainer = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  flex-grow: 1;
  flex-basis: 0;
  flex-shrink: 0;
  margin-top: 4px;

  button {
    flex-grow: 1;
    flex-basis: 0;
    flex-shrink: 0;
    padding: 0;
    border-radius: 2px !important;
  }

  &[data-selected="true"] {
    button {
      background-color: var(
        --fd-colors-component-selection-control-background-selected
      ) !important;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      width: 0.8px;
      height: 100%;
      background-color: var(--fd-colors-content-on-dark);
    }
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: row;
  box-shadow: var(--fd-shadows-elevations-bottom-low);
  overflow: hidden;

  ${ButtonContainer}:first-of-type button {
    border-left: 1px solid
      var(--fd-colors-component-selection-control-background-active);
  }

  button {
    border-top: 1px solid
      var(--fd-colors-component-selection-control-background-active);
    border-bottom: 1px solid
      var(--fd-colors-component-selection-control-background-active);
    border-right: 1px solid
      var(--fd-colors-component-selection-control-background-active);
    border-left: none;
  }
`;

export const AnimatedContainer = styled.div<{ open: boolean }>`
  overflow: hidden;
  transition:
    max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${({ open }) => (open ? "120px" : "0")};
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
`;
